/**
 * iOS BILLING BRIDGE (launch batch) — Premium on iPhone is bought through StoreKit 2 (App Store).
 *
 * The device never decides Premium: the signed transaction goes to the backend, which verifies it with
 * Apple's library and binds it to this account. A transaction is finished only after the backend accepts it.
 * Prices come from the App Store, never from a hard-coded string.
 */
import { registerPlugin } from '@capacitor/core';

const AppStoreBilling = registerPlugin('AppStoreBilling');

/** App Store localized prices for the configured memberships: { essential, premium } (displayPrice strings). */
export const fetchAppleProducts = async (products) => {
  const ids = Object.values(products || {}).filter(Boolean);
  if (!ids.length) return {};
  const { products: found } = await AppStoreBilling.getProducts({ productIds: ids });
  const byId = Object.fromEntries((found || []).map((p) => [p.productId, p.displayPrice]));
  return { essential: byId[products.essential], premium: byId[products.premium] };
};

/** App Store localized prices for any product ids: { productId: displayPrice }. */
export const fetchApplePrices = async (ids) => {
  if (!ids.length) return {};
  const { products: found } = await AppStoreBilling.getProducts({ productIds: ids });
  return Object.fromEntries((found || []).map((p) => [p.productId, p.displayPrice]));
};

export const purchaseApplePlan = async ({ productId, appAccountToken, verify }) => {
  const result = await AppStoreBilling.purchase({ productId, appAccountToken });
  if (result?.cancelled || result?.pending || !result?.signedTransaction) {
    return { cancelled: !result?.pending, pending: !!result?.pending };
  }
  const verified = await verify(result.signedTransaction);
  await AppStoreBilling.finish({ transactionId: result.transactionId });
  return verified;
};

/** Re-link the purchases this Apple ID currently holds, so a purchase is never orphaned. */
export const restoreApplePurchases = async (verify) => {
  const { signedTransactions } = await AppStoreBilling.restore();
  if (!signedTransactions?.length) return { restored: false };
  return verify(signedTransactions[0]);
};

/** Credit add-ons Apple charged but the app never finished; each is finished only after the server accepts it. */
export const recoverAppleAddons = async (addonIds, verify) => {
  const { transactions } = await AppStoreBilling.unfinished();
  let credited = 0;
  for (const t of transactions || []) {
    if (!addonIds.includes(t.productId)) continue;
    try {
      const r = await verify(t.signedTransaction);
      if (r?.credited) {
        await AppStoreBilling.finish({ transactionId: t.transactionId });
        credited += r.new ? 1 : 0;
      }
    } catch {
      // left unfinished: Apple keeps it, and the next visit or the server notification credits it
    }
  }
  return credited;
};

export const openAppleManage = () => AppStoreBilling.openManage();
